import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import productsData from "../data/products.json";

export default function SearchBar({ isOpen, onClose }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Get all products from all categories
  const allProducts = [
    ...productsData.mobiles,
    ...productsData.leds,
    ...productsData.homeAppliances,
    ...productsData.watches,
    ...productsData.audio,
    ...productsData.tablets,
    ...productsData.earbuds,
    ...productsData.soundbars,
    ...productsData.projectors
  ];

  // Handle search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = allProducts.filter(product => {
      return (
        product.name.toLowerCase().includes(query) ||
        product.category.toLowerCase().includes(query) ||
        product.brand?.toLowerCase().includes(query) ||
        product.specs?.some(spec => spec.toLowerCase().includes(query))
      );
    });

    setSearchResults(results.slice(0, 8)); // Show max 8 results
  }, [searchQuery]);

  // Close search when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle product click
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
    setSearchQuery("");
    setSearchResults([]);
    onClose();
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchResults.length > 0) {
      handleProductClick(searchResults[0].id);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="search-overlay">
      <div className="search-container" ref={searchRef}>
        <div className="search-input-wrapper">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search for Samsung products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            className="search-input"
          />
          <button onClick={onClose} className="search-close-btn">✕</button>
        </div>

        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((product) => (
              <div
                key={product.id}
                className="search-result-item"
                onClick={() => handleProductClick(product.id)}
              >
                <img src={product.image} alt={product.name} className="search-result-image" />
                <div className="search-result-info">
                  <h4>{product.name}</h4>
                  <p className="search-result-category">{product.category}</p>
                  <p className="search-result-price">PKR {product.price.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {searchQuery.trim() !== "" && searchResults.length === 0 && (
          <div className="search-no-results">
            <p>No products found for "{searchQuery}"</p>
            <p className="search-suggestion">Try searching for mobiles, TVs, watches, or other Samsung products</p>
          </div>
        )}
      </div>
    </div>
  );
}
